import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./section-curves.css";
import { Toaster } from "sonner";

import { AuthProvider, AdminEditProvider, DarkModeProvider } from "./contexts";
import { Header, Footer, PageNavigation } from "./components/layout";
import { PushNotificationPrompt } from "./components/common";

// Page imports
import { HomePage } from "./pages/HomePage";
import { Portal } from "./pages/Portal";
import { Dashboard } from "./pages/Dashboard";
import { ContactPage } from "./pages/ContactPage";
import { TeacherDashboard } from "./pages/TeacherDashboard";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AssignmentsPage } from "./pages/AssignmentsPage";
import {
  AboutPage, AnnouncementsPage, EventsPage, StaffPage, ResourcesPage,
  AttendancePage, GradesPage, TeacherAssignmentsPage, TeacherAttendancePage,
  TeacherGradesPage, ResourceInfoPage
} from "./pages/SimplePages";
import { MessagesPage } from "./pages/MessagesPage";
import { ParentPortal } from "./pages/ParentPortal";
import { FeesPage } from "./pages/FeesPage";
import { CourseCatalogPage } from "./pages/CourseCatalogPage";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { AlumniPortal } from "./pages/AlumniPortal";
import { AssignmentSubmission } from "./pages/AssignmentSubmission";
import { EnrollmentForm } from "./pages/EnrollmentForm";
import { ReportCardsPage } from "./pages/ReportCardsPage";
import GalleryPage from "./pages/GalleryPage";
import ClassAssignmentsPage from "./pages/ClassAssignmentsPage";
import { KGSubjectPage } from "./pages/KGSubjectPages";
import { BasicSchoolSubjectPage } from "./pages/BasicSchoolSubjectPages";
import { JHSSubjectPage } from "./pages/JHSSubjectPages";
import { JHSExamPrepPage } from "./pages/JHSExamPrepPage";
import {
  MedicalReleaseForm, FieldTripPermission, TranscriptRequest, ScheduleChangeForm,
  BusRoutes, ParkingInfo, CarpoolProgram, TrafficGuidelines,
  LunchMenu, NutritionInfo, MealPlans, AllergyGuidelines,
  TutoringServices, StudyGuides, ResearchDatabases, AcademicCalendar,
  NurseOffice, ImmunizationRequirements, EmergencyProcedures, MentalHealthResources,
  StudentHandbook, DressCode, TechnologyPolicy, AttendancePolicy, AntiBullyingPolicy
} from "./pages/ResourcePages";
import {
  AcademicCalendarPage, LibraryPortalPage, CollegePrepPage, AthleticsPage,
  CampusMapPage, BellSchedulePage, FormsDocumentsPage, FAQPage
} from "./pages/QuickAccessPages";
import {
  STEMActivitiesPage, SchoolAgendaPage, GamesPage, SchoolClubsPage,
  BlogPage, SchoolPodcastPage, SchoolTVPage, PodcastEpisodePage
} from "./pages/QuickAccessPages2";
import ClubDetailPage from "./pages/ClubDetailPage";
import { RemoteEducationPage, RobotTeacherPage, RoboticsPage } from "./pages/InteractivePages";
import AchievementWallPage from "./pages/AchievementWallPage";
import DigitalIDCardPage from "./pages/DigitalIDCardPage";
import StudyTimerPage from "./pages/StudyTimerPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ShoutoutBoardPage from "./pages/ShoutoutBoardPage";
import AppointmentBookingPage from "./pages/AppointmentBookingPage";
import YearbookPage from "./pages/YearbookPage";
import VirtualTourPage from "./pages/VirtualTourPage";
import PortfolioPage from "./pages/PortfolioPage";
import QuizPage from "./pages/QuizPage";
import SMSNotificationsPage from "./pages/SMSNotificationsPage";

const AppLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <PageNavigation />
    <Footer />
    <PushNotificationPrompt />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <DarkModeProvider>
      <AuthProvider>
        <AdminEditProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
            <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
            <Route path="/announcements" element={<AppLayout><AnnouncementsPage /></AppLayout>} />
            <Route path="/events" element={<AppLayout><EventsPage /></AppLayout>} />
            <Route path="/staff" element={<AppLayout><StaffPage /></AppLayout>} />
            <Route path="/resources" element={<AppLayout><ResourcesPage /></AppLayout>} />
            <Route path="/gallery" element={<AppLayout><GalleryPage /></AppLayout>} />
            <Route path="/academic-calendar" element={<AppLayout><AcademicCalendarPage /></AppLayout>} />
            <Route path="/library" element={<AppLayout><LibraryPortalPage /></AppLayout>} />
            <Route path="/jhs-exams-prep" element={<AppLayout><CollegePrepPage /></AppLayout>} />
            <Route path="/athletics" element={<AppLayout><AthleticsPage /></AppLayout>} />
            <Route path="/campus-map" element={<AppLayout><CampusMapPage /></AppLayout>} />
            <Route path="/bell-schedule" element={<AppLayout><BellSchedulePage /></AppLayout>} />
            <Route path="/forms-documents" element={<AppLayout><FormsDocumentsPage /></AppLayout>} />
            <Route path="/faq" element={<AppLayout><FAQPage /></AppLayout>} />
            <Route path="/about" element={<AppLayout><AboutPage /></AppLayout>} />
            <Route path="/messages" element={<AppLayout><MessagesPage /></AppLayout>} />
            <Route path="/assignments" element={<AppLayout><AssignmentsPage /></AppLayout>} />
            <Route path="/attendance" element={<AppLayout><AttendancePage /></AppLayout>} />
            <Route path="/grades" element={<AppLayout><GradesPage /></AppLayout>} />
            <Route path="/report-cards" element={<AppLayout><ReportCardsPage /></AppLayout>} />
            <Route path="/teacher-dashboard" element={<AppLayout><TeacherDashboard /></AppLayout>} />
            <Route path="/teacher-assignments" element={<AppLayout><TeacherAssignmentsPage /></AppLayout>} />
            <Route path="/teacher-attendance" element={<AppLayout><TeacherAttendancePage /></AppLayout>} />
            <Route path="/teacher-grades" element={<AppLayout><TeacherGradesPage /></AppLayout>} />
            <Route path="/admin-dashboard" element={<AppLayout><AdminDashboard /></AppLayout>} />
            <Route path="/parent-portal" element={<AppLayout><ParentPortal /></AppLayout>} />
            <Route path="/fees" element={<AppLayout><FeesPage /></AppLayout>} />
            <Route path="/fees/success" element={<AppLayout><FeesPage /></AppLayout>} />
            <Route path="/course-catalog" element={<AppLayout><CourseCatalogPage /></AppLayout>} />
            <Route path="/kg-subject/:subject" element={<AppLayout><KGSubjectPage /></AppLayout>} />
            <Route path="/basic-subject/:subject" element={<AppLayout><BasicSchoolSubjectPage /></AppLayout>} />
            <Route path="/jhs-subject/:subject" element={<AppLayout><JHSSubjectPage /></AppLayout>} />
            <Route path="/jhs-subject/:subject/exam-prep/:prepId" element={<AppLayout><JHSExamPrepPage /></AppLayout>} />
            <Route path="/resources/course-catalog" element={<AppLayout><CourseCatalogPage /></AppLayout>} />
            <Route path="/alumni" element={<AppLayout><AlumniPortal /></AppLayout>} />
            <Route path="/assignment/:assignmentId" element={<AppLayout><AssignmentSubmission /></AppLayout>} />
            <Route path="/resources/enrollment-application" element={<AppLayout><EnrollmentForm /></AppLayout>} />
            <Route path="/assignments/:classId" element={<AppLayout><ClassAssignmentsPage /></AppLayout>} />
            <Route path="/resources/medical-release" element={<AppLayout><MedicalReleaseForm /></AppLayout>} />
            <Route path="/resources/field-trip-permission" element={<AppLayout><FieldTripPermission /></AppLayout>} />
            <Route path="/resources/transcript-request" element={<AppLayout><TranscriptRequest /></AppLayout>} />
            <Route path="/resources/schedule-change" element={<AppLayout><ScheduleChangeForm /></AppLayout>} />
            <Route path="/resources/bus-routes" element={<AppLayout><BusRoutes /></AppLayout>} />
            <Route path="/resources/parking-info" element={<AppLayout><ParkingInfo /></AppLayout>} />
            <Route path="/resources/carpool-program" element={<AppLayout><CarpoolProgram /></AppLayout>} />
            <Route path="/resources/traffic-guidelines" element={<AppLayout><TrafficGuidelines /></AppLayout>} />
            <Route path="/resources/lunch-menu" element={<AppLayout><LunchMenu /></AppLayout>} />
            <Route path="/resources/nutrition-info" element={<AppLayout><NutritionInfo /></AppLayout>} />
            <Route path="/resources/meal-plans" element={<AppLayout><MealPlans /></AppLayout>} />
            <Route path="/resources/allergy-guidelines" element={<AppLayout><AllergyGuidelines /></AppLayout>} />
            <Route path="/resources/tutoring" element={<AppLayout><TutoringServices /></AppLayout>} />
            <Route path="/resources/study-guides" element={<AppLayout><StudyGuides /></AppLayout>} />
            <Route path="/resources/research-databases" element={<AppLayout><ResearchDatabases /></AppLayout>} />
            <Route path="/resources/academic-calendar" element={<AppLayout><AcademicCalendar /></AppLayout>} />
            <Route path="/resources/nurse-office" element={<AppLayout><NurseOffice /></AppLayout>} />
            <Route path="/resources/immunization" element={<AppLayout><ImmunizationRequirements /></AppLayout>} />
            <Route path="/resources/emergency-procedures" element={<AppLayout><EmergencyProcedures /></AppLayout>} />
            <Route path="/resources/mental-health" element={<AppLayout><MentalHealthResources /></AppLayout>} />
            <Route path="/resources/student-handbook" element={<AppLayout><StudentHandbook /></AppLayout>} />
            <Route path="/resources/dress-code" element={<AppLayout><DressCode /></AppLayout>} />
            <Route path="/resources/technology-policy" element={<AppLayout><TechnologyPolicy /></AppLayout>} />
            <Route path="/resources/attendance-policy" element={<AppLayout><AttendancePolicy /></AppLayout>} />
            <Route path="/resources/anti-bullying" element={<AppLayout><AntiBullyingPolicy /></AppLayout>} />
            <Route path="/resources/*" element={<AppLayout><ResourceInfoPage title="Resource" description="Resource information" content={<p>This resource page is under construction.</p>} /></AppLayout>} />
            <Route path="/stem-activities" element={<AppLayout><STEMActivitiesPage /></AppLayout>} />
            <Route path="/school-agenda" element={<AppLayout><SchoolAgendaPage /></AppLayout>} />
            <Route path="/games" element={<AppLayout><GamesPage /></AppLayout>} />
            <Route path="/school-clubs" element={<AppLayout><SchoolClubsPage /></AppLayout>} />
            <Route path="/school-clubs/:clubId" element={<AppLayout><ClubDetailPage /></AppLayout>} />
            <Route path="/blog" element={<AppLayout><BlogPage /></AppLayout>} />
            <Route path="/school-podcast" element={<AppLayout><SchoolPodcastPage /></AppLayout>} />
            <Route path="/school-podcast/:episodeId" element={<AppLayout><PodcastEpisodePage /></AppLayout>} />
            <Route path="/school-tv" element={<AppLayout><SchoolTVPage /></AppLayout>} />
            <Route path="/remote-education" element={<AppLayout><RemoteEducationPage /></AppLayout>} />
            <Route path="/robot-teacher" element={<AppLayout><RobotTeacherPage /></AppLayout>} />
            <Route path="/robotics" element={<AppLayout><RoboticsPage /></AppLayout>} />
            <Route path="/achievements" element={<AppLayout><AchievementWallPage /></AppLayout>} />
            <Route path="/my-id-card" element={<AppLayout><DigitalIDCardPage /></AppLayout>} />
            <Route path="/study-timer" element={<AppLayout><StudyTimerPage /></AppLayout>} />
            <Route path="/leaderboard" element={<AppLayout><LeaderboardPage /></AppLayout>} />
            <Route path="/shoutout-board" element={<AppLayout><ShoutoutBoardPage /></AppLayout>} />
            <Route path="/appointments" element={<AppLayout><AppointmentBookingPage /></AppLayout>} />
            <Route path="/yearbook" element={<AppLayout><YearbookPage /></AppLayout>} />
            <Route path="/virtual-tour" element={<AppLayout><VirtualTourPage /></AppLayout>} />
            <Route path="/portfolio" element={<AppLayout><PortfolioPage /></AppLayout>} />
            <Route path="/quizzes" element={<AppLayout><QuizPage /></AppLayout>} />
            <Route path="/sms-notifications" element={<AppLayout><SMSNotificationsPage /></AppLayout>} />
          </Routes>
        </AdminEditProvider>
      </AuthProvider>
      </DarkModeProvider>
    </BrowserRouter>
  );
}

export default App;
